/**
 * Post-match insight types and emoji mapping.
 *
 * Lives outside usePostMatchAnalysis so the WhatsApp summary formatter can be
 * used without React or the browser Supabase client (the WhatsApp bot imports it).
 */

export interface PostMatchInsight {
  id: string;
  analysisType: string;
  priority: number;
  headline: string;
  details: Record<string, unknown>;
  playerIds: string[];
  createdAt: string;
}

export interface ConfidenceThreshold {
  insightCategory: string;
  lowThreshold: number;
  highThreshold: number;
}

/**
 * Get emoji for analysis type
 */
export const getInsightEmoji = (analysisType: string): string => {
  const emojis: Record<string, string> = {
    // Trophy insights
    'trophy_change': '👑',
    'trophy_new': '🏆',
    'trophy_extended': '📈',
    // Rivalry insights
    'rivalry_first_win': '🎯',
    'rivalry_perfect': '💯',
    'rivalry_dominant': '💪',
    'rivalry_nemesis': '😈',
    'rivalry_close': '⚔️',
    'rivalry_revenge': '🔥',
    'rivalry_ongoing_drought': '🏜️',
    'never_beaten_rivalry': '😰',
    'first_ever_win_nemesis': '🎉',
    // Partnership insights
    'partnership_first': '🤝',
    'partnership_milestone': '👯',
    // Chemistry insights
    'chemistry_kings': '🔗',
    'chemistry_curse': '💀',
    'chemistry_milestone': '🎊',
    // Trio insights
    'trio_dream_team': '👑',
    'trio_cursed': '☠️',
    // Individual streak insights
    'win_streak': '🔥',
    'win_streak_ended': '💔',
    'losing_streak': '❄️',
    'losing_streak_ended': '🎉',
    'unbeaten_streak': '🛡️',
    'unbeaten_streak_ended': '💔',
    'winless_streak': '🏜️',
    'winless_streak_ended': '💧',
    // Team streak insights
    'streak_milestone': '🔥',
    'streak_broken': '💔',
    'team_streak': '📊',
    // Attendance streak insights
    'attendance_streak': '🏃',
    'attendance_streak_ended': '😢',
    // Other insights
    'cap_milestone': '🎖️',
    'personal_best': '⭐',
    'personal_best_streak': '📈',
    'game_record': '⚽',
    'man_down_win': '💪',
    // New v2 insights
    'debut_appearance': '⭐',
    'return_after_absence': '👋',
    'first_game_back_win': '🙌',
    'bench_warmer_promoted': '📣',
    'team_color_loyalty': '💙',
    'team_color_switch': '🔄',
    'blowout_game': '💥',
    'shutout_game': '🧤',
    // Phase 3 insights - Year-over-year awards
    'award_defending_champion': '🏆',
    // Phase 3 insights - Goal scoring patterns
    'low_scoring_game': '🧱',
    'team_best_score': '📈',
    // Phase 3 insights - Team color trends
    'team_color_dominance': '💪',
    'team_color_streak_broken': '⛔',
    'player_color_curse': '🎭',
    // Phase 3 insights - Injury token stats
    'injury_token_used': '🏥',
    'injury_token_return': '💉',
  };
  return emojis[analysisType] || '📊';
};
